import { IMenuItem } from 'models/IMenuItem';
import { NotifyLink } from 'components/Shared/NotifyLink';
import styles from './styles.scss';
import { useState } from 'react';
import cn from 'classnames';
import { isAllCategoriesMenuItem } from 'components/MainMenu/selectors';
import { IconArrow } from 'components/Shared/Icon';
import { rm, hasElements } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { useTouchStartNavigate } from 'components/MainMenu/useTouchStartNavigate';
import { Grid, Cell } from 'components/Grid';
import { useDispatch } from 'react-redux';
import { GtmHeaderNavClick } from 'store/googleTagManager/actions';

interface OwnProps extends IMenuItem {
  openMenu: () => void;
  closeMenu: () => void;
  isOpen: boolean;
  isLoading: boolean;
}

type CategoryProps = Pick<IMenuItem, 'subCategories'> & {
  onNavItemClick: (url: string) => void;
};

const SimpleCategories = ({ subCategories, onNavItemClick }: CategoryProps) => (
  <ul className={cn(styles.subCategories, styles.simple)}>
    {subCategories.map(category => (
      <li className={cn(styles.subCategory, styles.simple)} key={category.title}>
        <NotifyLink
          className={styles.subCategoryLink}
          ticket={category.ticket}
          url={category.url}
          onClick={() => onNavItemClick(category.url)}
          {...getQaDataAttr(`link-nav-${category.id}`)}
        >
          {category.title}
        </NotifyLink>
      </li>
    ))}
  </ul>
);

const DetailedCategories = ({ subCategories, onNavItemClick }: CategoryProps) => (
  <div className={cn(styles.subCategories, styles.detailed)}>
    <Grid gutters="extraLarge" large="4" desktop="5" huge="6">
      {subCategories.map(category => (
        <Cell key={category.id} className={cn(styles.subCategory, styles.detailed)}>
          <NotifyLink
            className={cn(styles.subCategoryLink, styles.title)}
            ticket={category.ticket}
            url={category.url}
            onClick={() => onNavItemClick(category.url)}
            {...getQaDataAttr(`link-nav-${category.id}`)}
          >
            {category.title}
          </NotifyLink>
          <ul className={styles.allCategoriesList}>
            {category.subCategories.map(subCategory => (
              <li key={subCategory.title}>
                <NotifyLink
                  className={styles.subCategoryLink}
                  ticket={subCategory.ticket}
                  url={subCategory.url}
                  onClick={() => onNavItemClick(subCategory.url)}
                  {...getQaDataAttr(`link-nav-${subCategory.id}`)}
                >
                  {subCategory.title}
                </NotifyLink>
              </li>
            ))}
            {category.subCategoriesCount &&
              category.subCategoriesCount > category.subCategories.length && (
                <li>
                  <NotifyLink
                    className={cn(styles.subCategoryLink, styles.all)}
                    ticket={category.ticket}
                    url={category.url}
                    onClick={() => onNavItemClick(category.url)}
                    {...getQaDataAttr(`link-nav-${category.id}`)}
                  >
                    {rm('navigation_seeallcountcategories', category.subCategoriesCount)}
                    &nbsp;
                    <IconArrow size="tiny" />
                  </NotifyLink>
                </li>
              )}
          </ul>
        </Cell>
      ))}
    </Grid>
  </div>
);

export const Section = ({
  title,
  openMenu,
  closeMenu,
  isOpen,
  isLoading,
  subCategories,
  id,
  ...props
}: OwnProps) => {
  const showAllCategories = isAllCategoriesMenuItem(id);
  const reduxDispatch = useDispatch();
  const [, setRef] = useTouchStartNavigate<HTMLAnchorElement | undefined>(useState(), {
    isLoading,
    isOpen,
    openMenu,
    closeMenu
  });
  const SubCategories = showAllCategories ? DetailedCategories : SimpleCategories;
  const hasSubcategories = hasElements(subCategories);
  const showSubcategories = isOpen && hasSubcategories;

  const handleOnClick = (url: string) => {
    reduxDispatch(GtmHeaderNavClick(url));
  };

  return (
    <div onMouseOver={openMenu} onMouseOut={closeMenu} className={styles.section}>
      <NotifyLink
        className={cn(styles.sectionLink, { [styles.open]: showSubcategories })}
        onClick={() => handleOnClick(props.url)}
        ticket={props.ticket}
        url={props.url}
        setRef={setRef}
        {...getQaDataAttr(`link-nav-${id}`)}
      >
        <span className={styles.sectionTitle}>{title}</span>
      </NotifyLink>
      {showSubcategories && (
        <SubCategories onNavItemClick={handleOnClick} subCategories={subCategories} />
      )}
    </div>
  );
};
